import type { FC } from 'react';
import { useState } from 'react';
import { useUserStore } from '@Auth/store';
import Button from '@components/Button';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import Modal from '@components/Modal';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useAllTeams } from '@queries/fetchTeams';
import { useCreateUser, useUpdateUser } from '@queries/fetchUsers';
import { createUserPayloadSchema, updateUserPayloadSchema } from '@root/@types/ajv/user';
import { ApiPatchOperation, IdentityProvider, UserRole } from '@root/@types/types';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';
import clsx from 'clsx';

interface UserModalProps {
  handleClose: () => void;
  isAdd: boolean;
  user: CanonicalUserRecordWithCapabilities;
}

const USER_ROLES = [UserRole.Admin, UserRole.User, UserRole.Superadmin];

const UserModal: FC<UserModalProps> = ({ user, handleClose, isAdd }) => {
  const [formValues, setFormValues] = useState(user ?? {});
  const { user: currentUser } = useUserStore(({ user }) => ({ user }));
  const { refreshUser } = useUserStore(({ refreshUser }) => ({
    refreshUser,
  }));

  const getFeatureFlagState = useFeatureFlags();

  const { data: allTeams = [], isFetching: isFetchingTeams } = useAllTeams();

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;

  const isSSOEnabled = getFeatureFlagState('SSO');
  const externalIdentityProvider = import.meta.env.VITE_EXTERNAL_IDENTITY_PROVIDER;

  const identityProviders = [
    {
      id: IdentityProvider.Internal,
      name: 'Username & Password',
      description: 'Users can sign up with a username and password combination',
    },
    {
      id: IdentityProvider.External,
      name: 'Single Sign On',
      description: "The user can sign up with their organisation's identity provider account",
    },
  ];

  const handleChange = (name: string) => (event: any) => {
    if (name === 'teams') {
      setFormValues({
        ...formValues,
        [name]: event?.map((teamName: string) => allTeams?.find((team) => team.name === teamName)?.id),
      });
    } else {
      setFormValues({
        ...formValues,
        [name]: name === 'role' || name === 'identityProvider' ? event : event.target.value,
      });
    }
  };

  const handleSubmit = () => {
    const { username, firstName, lastName, email, role, teams, identityProvider } = formValues;

    if (isAdd) {
      createUserMutation.mutate(formValues, { onSuccess: handleClose });
    } else {
      updateUserMutation.mutate(
        {
          username,
          payload: {
            operation: ApiPatchOperation.UserUpdate,
            firstName,
            lastName,
            email,
            role,
            teams,
            identityProvider,
          },
        },
        {
          onSuccess: async (updatedUser) => {
            if (currentUser?.username === username) {
              await refreshUser(updatedUser.updated_at);
            }
            handleClose();
          },
        },
      );
    }
  };

  const skipToggleEnableDisableOnUser = !isAdd && !user?.capabilitiesManager?.canBeEnabledOrDisabled;

  const handleToggleEnabledStateOnUser = () => {
    if (skipToggleEnableDisableOnUser) {
      return;
    }

    const { enabled, username } = formValues;

    handleClose();
    updateUserMutation.mutate({
      username,
      payload: { operation: ApiPatchOperation.UserChangeState, enabled: !enabled },
    });
  };

  const isSubmitDisabled = (requiredFields: string[], values: any) =>
    requiredFields.find((fieldName: string) => !values?.[fieldName]?.trim()) !== undefined;

  return (
    <Modal
      open
      className="flex w-full flex-col gap-y-2 sm:w-5/6 sm:max-w-full lg:w-1/2"
      onClose={handleClose}
      title={isAdd ? 'Create user' : 'Edit user'}
    >
      <InputText
        className="mt-2"
        labelText="First Name"
        isRequired
        value={formValues.firstName}
        onChange={handleChange('firstName')}
      />
      <InputText labelText="Last Name" isRequired value={formValues.lastName} onChange={handleChange('lastName')} />
      <InputText
        labelText="Email"
        isRequired
        value={formValues.email}
        readOnly={!isAdd}
        onChange={isAdd ? handleChange('email') : () => {}}
      />
      <InputSelect
        labelText="Role"
        isRequired
        shouldRenderInsidePortal
        options={USER_ROLES.map((i) => ({ name: i, value: i }))}
        onChange={handleChange('role')}
        value={formValues.role}
      />
      {allTeams.length > 0 && (
        <InputMultiSelect
          labelText="Teams"
          shouldRenderInsidePortal
          options={allTeams.map((team) => team.name)}
          isDisabled={isFetchingTeams}
          onChange={handleChange('teams')}
          onSelect={() => {}}
          value={formValues.teams?.map((teamId) => allTeams?.find((team) => team.id === teamId)?.name ?? '')}
        />
      )}
      {isSSOEnabled && externalIdentityProvider && (
        <InputSelect
          labelText="Sign Up Method"
          shouldRenderInsidePortal
          isLoading={isFetchingTeams}
          isReadOnly={!isAdd}
          options={identityProviders.map((i) => ({ name: i.name, value: i.id }))}
          onChange={isAdd ? handleChange('identityProvider') : () => {}}
          value={identityProviders?.find((i) => i.id === formValues?.identityProvider)?.name ?? ''}
        />
      )}
      {isAdd ? (
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            disabled={isSubmitDisabled(createUserPayloadSchema.required, formValues) || isLoading}
            onClick={handleSubmit}
            type="button"
            className="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Create
          </button>
          <button
            onClick={handleClose}
            type="button"
            className="border-info-300 text-info-700 hover:bg-info-50 focus:ring-primary-500 mt-3 inline-flex h-[38px] w-full justify-center rounded-md border bg-white px-4 py-2 text-base font-medium shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:mt-0 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-row">
          <div className="w-1/2 basis-1/2">
            <div className="mt-5 sm:mt-4 sm:flex">
              <Button
                className={clsx(
                  'inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:w-auto sm:text-sm',
                )}
                isDisabled={isLoading || skipToggleEnableDisableOnUser}
                onClick={handleToggleEnabledStateOnUser}
                type="button"
                kind={formValues.enabled ? 'danger' : 'green'}
              >
                {formValues.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
          <div className="w-1/2 basis-1/2">
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <Button
                className="focus:ring-primary-500 inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:text-white sm:ml-3 sm:w-auto sm:text-sm"
                loading={isLoading}
                kind="primary"
                onClick={handleSubmit}
                type="button"
                isDisabled={isSubmitDisabled(updateUserPayloadSchema.required, {
                  ...formValues,
                  operation: ApiPatchOperation.UserUpdate,
                })}
              >
                Submit
              </Button>
              <Button
                className="border-info-300 text-info-700 hover:bg-info-50 focus:ring-primary-500 mt-3 inline-flex h-[38px] w-full justify-center rounded-md border bg-white px-4 py-2 text-base font-medium shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:mt-0 sm:w-auto sm:text-sm"
                isDisabled={isLoading}
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserModal;
